"use client";
import { useEffect, useCallback } from "react";
import { usePathname } from "next/navigation";
import useIsClient from "@/hooks/useIsClient";
import useShouldFire from "./useShouldFire";

interface TwqEventParams {
  value?: number;
  currency?: string;
  conversion_id?: string;
  contents?: Array<{
    content_id?: string;
    content_name?: string;
    content_price?: number | string;
    num_items?: number | string;
  }>;
  email_address?: string;
  phone_number?: string;
  status?: string;
  search_string?: string;
  description?: string;
}

// Module-level flag — survives re-renders and component remounts
// but resets on full page reload (which is correct behaviour)
let pageViewFiredForPath: string | null = null;

interface UseXPixelOptions {
  pageViewEventId?: string;
}

export default function useXPixel(options?: UseXPixelOptions) {
  const pathname = usePathname();
  const client = useIsClient();
  const shouldFire = useShouldFire();
  const { pageViewEventId } = options || {};

  // Track PageView on route changes, but only once per path
  useEffect(() => {
    if (!shouldFire || !pathname || !client) return;

    // Guard: don't fire twice for the same path
    if (pageViewFiredForPath === pathname) return;

    const twq = (window as any).twq;
    if (!twq) return;

    pageViewFiredForPath = pathname;
    // Use custom page view event ID if provided, otherwise use default "PageView"
    const eventName = pageViewEventId || "PageView";
    twq("track", eventName);
  }, [pathname, shouldFire, client, pageViewEventId]);

  const trackEvent = useCallback(
    (eventName: string, params?: TwqEventParams, eventId?: string) => {
      if (typeof window === "undefined") return;

      const twq = (window as any).twq;
      if (!twq) return;

      const eventParams = { ...params };
      if (eventId) {
        eventParams.conversion_id = eventId;
      }

      twq("event", eventName, eventParams);

      if (eventName === "PURCHASE" || eventName.includes("PURCHASE")) {
        fetch("/api/log-info", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: "X/Twitter Pixel Purchase Event",
            details: { event_name: "Purchase", event_id: eventId, ...params },
            host: window.location.hostname,
            url: window.location.pathname,
          }),
        }).catch((e) =>
          console.error("Error logging X Pixel purchase event", e),
        );
      }
    },
    [],
  );

  return {
    trackEvent,
    initialized: typeof window !== "undefined" && !!(window as any).twq,
  };
}
